import type { PrimerSideEstimate } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { cleanPrimer } from '../../Util/orderSettingDetection/iupac.ts'
import { stripAdapter } from '../../Util/orderSettingDetection/primerRegistry.ts'

export type PrimerComparison =
  | { kind: 'agree' }
  | { kind: 'tag-mismatch'; readsTag: string; sheetTag: string }
  | { kind: 'sheet-untagged'; readsTag: string }
  | { kind: 'gene-spelling' }
  | { kind: 'no-estimate' }
  | { kind: 'no-sheet' }

/**
 * Whether a sheet primer and the reads' estimate agree, and if not, how. Only a confident estimate can
 * disagree. Following the prod measurement, a sheet tag that the reads carry minus its first one or two
 * bases (the read starts inside the tag) is not a mismatch, and an untagged sheet primer is reported
 * separately, since trimming works without the tag.
 */
export const comparePrimer = (estimate: PrimerSideEstimate | undefined, sheetPrimer: string | undefined): PrimerComparison => {
  if (!sheetPrimer) {
    return { kind: 'no-sheet' }
  }
  if (!estimate || estimate.confidence !== 'high' || estimate.primer === null || estimate.gene === null || estimate.prefix === null) {
    return { kind: 'no-estimate' }
  }
  const sheet = stripAdapter(sheetPrimer)
  if (sheet === cleanPrimer(estimate.primer)) {
    return { kind: 'agree' }
  }
  const sheetTag = sheet.length > estimate.gene.length ? sheet.slice(0, sheet.length - estimate.gene.length) : ''
  const readsTag = estimate.prefix
  if (sheetTag === readsTag) {
    return { kind: 'gene-spelling' }
  }
  if (sheetTag === '') {
    return readsTag === '' ? { kind: 'gene-spelling' } : { kind: 'sheet-untagged', readsTag }
  }
  if (sheetTag.endsWith(readsTag) && sheetTag.length - readsTag.length <= 2) {
    return { kind: 'agree' }
  }
  return { kind: 'tag-mismatch', readsTag, sheetTag }
}

/** A confident estimate can pre-fill a sample's primers when the sheet declares none. */
export const prefillable = (estimate: { forward: PrimerSideEstimate; reverse: PrimerSideEstimate } | undefined): boolean =>
  !!estimate &&
  estimate.forward.confidence === 'high' &&
  estimate.reverse.confidence === 'high' &&
  !!estimate.forward.primer &&
  !!estimate.reverse.primer

/**
 * How the estimator did against the primers a sheet declares: the telemetry outcome categories.
 * `sheet_untagged` is apart from `tag_mismatch`: an untagged entry often still works in the pipeline,
 * so counting it as a miss would overstate the estimator's error rate.
 */
export type EstimateOutcome = 'correct' | 'tag_mismatch' | 'sheet_untagged' | 'gene_spelling' | 'no_estimate'

/**
 * One sample's verdict over both sides, worst side first: no estimate, a wrong tag, an untagged sheet
 * entry, then a differently spelled gene part. `null` when the sheet does not declare both primers, which leaves
 * nothing to check against.
 */
export const estimateOutcome = (
  estimate: { forward: PrimerSideEstimate; reverse: PrimerSideEstimate } | undefined,
  sheet: { forward?: string; reverse?: string }
): EstimateOutcome | null => {
  if (!sheet.forward || !sheet.reverse) {
    return null
  }
  const sides = [comparePrimer(estimate?.forward, sheet.forward), comparePrimer(estimate?.reverse, sheet.reverse)]
  if (sides.some((side) => side.kind === 'no-estimate')) {
    return 'no_estimate'
  }
  if (sides.some((side) => side.kind === 'tag-mismatch')) {
    return 'tag_mismatch'
  }
  if (sides.some((side) => side.kind === 'sheet-untagged')) {
    return 'sheet_untagged'
  }
  if (sides.some((side) => side.kind === 'gene-spelling')) {
    return 'gene_spelling'
  }
  return 'correct'
}
